import { validHealthDay, healthDay } from "./healthStatistics"
import { healthWorkoutWindow } from "./healthImportMatching"

export const SLEEP_PHASES = [
  { key: "awake", label: "Wach", color: "#e6b8a3" },
  { key: "rem", label: "REM", color: "#d8bae5" },
  { key: "light", label: "Leicht", color: "#a5a0da" },
  { key: "deep", label: "Tief", color: "#7786c6" },
  { key: "sleeping", label: "Schlaf ohne Phase", color: "#aaa6b8" },
  { key: "outOfBed", label: "Ausserhalb des Betts", color: "#c7ab96" },
  { key: "unknown", label: "Nicht zugeordnet", color: "#727278" },
]
const asleepKinds = new Set(["rem", "light", "deep", "sleeping"])
const number = value => typeof value === "number" && Number.isFinite(value)
const timestamp = value => number(value) && value > 0 && value < 8640000000000000
const text = (value, length = 120) => typeof value === "string" ? value.trim().slice(0, length) : ""

export function parseSleepSessions(raw, from, to, stages = false) {
  const seen = new Set()
  return (Array.isArray(raw) ? raw : []).slice(0, 200).flatMap(item => {
    if (!item || !timestamp(item.start) || !timestamp(item.end) || item.start >= item.end || item.end <= from || item.start >= to || !validHealthDay(item.day)) return []
    const source = text(item.source, 254), id = text(item.id, 254)
    const key = `${source}|${id}`
    if (!source || !id || seen.has(key)) return []
    seen.add(key)
    const start = Math.max(from, item.start), end = Math.min(to, item.end)
    const session = { key, id, source, sourceName: text(item.sourceName) || source, day: item.day, start, end,
      clipped: item.clipped === true || start !== item.start || end !== item.end, stagesTruncated: item.stagesTruncated === true, stages: [] }
    if (stages) {
      const entries = Array.isArray(item.stages) ? item.stages : []
      session.stagesTruncated = session.stagesTruncated || entries.length > 1500
      session.stages = entries.slice(0, 1500).flatMap(stage => {
        if (!stage || !timestamp(stage.start) || !timestamp(stage.end) || stage.start >= stage.end || stage.end <= start || stage.start >= end) return []
        return [{ start: Math.max(start, stage.start), end: Math.min(end, stage.end),
          kind: SLEEP_PHASES.some(phase => phase.key === stage.kind) ? stage.kind : "unknown" }]
      })
    }
    return [session]
  }).sort((a, b) => a.start - b.start || a.key.localeCompare(b.key))
}

export function parseHealthDetails(raw, day, categories) {
  if (!raw || !validHealthDay(day) || raw.day !== day || !timestamp(raw.syncedAt)
    || ![raw.dayStart, raw.dayEnd, raw.sleepStart, raw.sleepEnd].every(timestamp)
    || raw.dayStart > raw.dayEnd || raw.sleepStart >= raw.sleepEnd || raw.sleepStart > raw.dayStart
    || raw.sleepEnd > raw.dayEnd || raw.dayEnd - raw.dayStart > 26 * 3600000 || raw.sleepEnd - raw.sleepStart > 26 * 3600000) {
    throw new Error("Die Detaildaten konnten nicht gelesen werden.")
  }
  const readable = raw.readable === true
  const sleepAllowed = readable && categories.includes("sleep"), pulseAllowed = readable && categories.includes("heartRate")
  const sleepSessions = sleepAllowed ? parseSleepSessions(raw.sleepSessions, raw.sleepStart, raw.sleepEnd, true) : []
  const seenSources = new Set()
  const pulseSources = (pulseAllowed && Array.isArray(raw.pulseSources) ? raw.pulseSources : []).slice(0, 8).flatMap(item => {
    const source = text(item?.source, 254)
    if (!source || seenSources.has(source)) return []
    seenSources.add(source)
    const seenMinutes = new Set()
    const points = (Array.isArray(item.points) ? item.points : []).slice(0, 2000).filter(point => {
      if (!point || !timestamp(point.time) || !timestamp(point.end) || point.time < raw.sleepStart || point.end >= raw.dayEnd || point.end < point.time
        || Math.floor(point.time / 60000) !== Math.floor(point.end / 60000)
        || !Number.isInteger(point.count) || point.count < 1 || point.count > 100000
        || !number(point.sum) || !number(point.min) || !number(point.max) || point.min < 1 || point.max > 300 || point.min > point.max
        || point.sum / point.count < point.min || point.sum / point.count > point.max) return false
      const minute = Math.floor(point.time / 60000)
      if (seenMinutes.has(minute)) return false
      seenMinutes.add(minute)
      return true
    }).map(point => ({ time: point.time, end: point.end, value: point.sum / point.count,
      count: point.count, sum: point.sum, min: point.min, max: point.max })).sort((a, b) => a.time - b.time)
    return [{ source, sourceName: text(item.sourceName) || source, points }]
  })
  return { day, syncedAt: raw.syncedAt, timeZone: text(raw.timeZone), dayStart: raw.dayStart, dayEnd: raw.dayEnd,
    sleepStart: raw.sleepStart, sleepEnd: raw.sleepEnd, readable, sleepSessions, pulseSources,
    sleepTruncated: sleepAllowed && (raw.sleepTruncated === true || raw.sleepSessions?.length > 200),
    pulseTruncated: pulseAllowed && (raw.pulseTruncated === true || raw.pulseSources?.length > 8 || (Array.isArray(raw.pulseSources) && raw.pulseSources.some(source => source?.points?.length > 2000))) }
}

export function sleepBreakdown(session) {
  if (!session) return null
  // Sweep all stage boundaries: duplicate stages count once, conflicting stages become unknown.
  const events = new Map([[session.start, []], [session.end, []]])
  for (const stage of session.stages) {
    for (const [time, delta] of [[stage.start, 1], [stage.end, -1]]) {
      if (!events.has(time)) events.set(time, [])
      events.get(time).push([stage.kind, delta])
    }
  }
  const times = [...events.keys()].sort((a, b) => a - b), active = new Map(), segments = [], durations = {}
  for (let index = 0; index < times.length - 1; index++) {
    const start = times[index], end = times[index + 1]
    for (const [kind, delta] of events.get(start)) active.set(kind, (active.get(kind) || 0) + delta)
    const kinds = [...active.keys()].filter(kind => active.get(kind) > 0)
    const kind = kinds.length === 1 ? kinds[0] : "unknown"
    durations[kind] = (durations[kind] || 0) + (end - start) / 1000
    const previous = segments[segments.length - 1]
    if (previous?.kind === kind && previous.end === start) previous.end = end
    else segments.push({ start, end, kind })
  }
  const asleepSeconds = [...asleepKinds].reduce((sum, kind) => sum + (durations[kind] || 0), 0)
  const recordingSeconds = (session.end - session.start) / 1000
  return { segments, durations, asleepSeconds, recordingSeconds,
    complete: !durations.unknown && !session.clipped && !session.stagesTruncated,
    phases: SLEEP_PHASES.filter(phase => durations[phase.key] > 0).map(phase => ({ ...phase, seconds: durations[phase.key],
      percent: asleepKinds.has(phase.key) && asleepSeconds > 0 ? durations[phase.key] / asleepSeconds * 100 : null })) }
}

export function healthPulseWindow(source, from, to) {
  // A boundary minute is omitted if it also contains samples outside the selected interval.
  const points = (source?.points || []).filter(point => point.time >= from && point.end < to)
  const count = points.reduce((sum, point) => sum + point.count, 0)
  const segments = []
  for (const point of points) {
    const last = segments[segments.length - 1]
    if (!last || point.time - last[last.length - 1].end > 5 * 60000) segments.push([point])
    else last.push(point)
  }
  return { points, segments, count, average: count ? points.reduce((sum, point) => sum + point.sum, 0) / count : null,
    minimum: points.length ? Math.min(...points.map(point => point.min)) : null,
    maximum: points.length ? Math.max(...points.map(point => point.max)) : null }
}

export function healthTrainingSessions(grouped, owner, day) {
  if (!owner || !validHealthDay(day)) return []
  const seen = new Set()
  return (Array.isArray(grouped) ? grouped : []).flatMap(group => {
    const execution = group?.execution
    if (!execution || String(execution.user_id?._id || execution.user_id || "") !== String(owner)) return []
    const id = String(execution._id || "")
    if (!id || seen.has(id)) return []
    seen.add(id)
    const recorded = Number(execution.ts) > 0 ? Number(execution.ts) * 1000 : Date.parse(execution.createdAt || execution.endedAt)
    if (!timestamp(recorded) || healthDay(new Date(recorded)) !== day) return []
    const window = healthWorkoutWindow(execution, recorded)
    const exact = window.exact && healthDay(new Date(window.to)) === day && window.to - window.from <= 24 * 3600000
    const duration = Number(execution.duration), volume = Number(execution.liftedWeight)
    return [{ id, title: text(execution.title) || "Training", from: window.from, to: window.to, exact,
      duration: number(duration) && duration > 0 ? duration : null,
      volume: number(volume) && volume > 0 ? volume : null }]
  }).sort((a, b) => a.to - b.to)
}

export function sleepRhythm(sessions, days, source) {
  // Show one main recording per day and source, never add overlapping nights from multiple apps.
  return days.filter(day => !day.future).map(day => {
    const recordings = sessions.filter(session => session.day === day.day && session.source === source)
      .sort((a, b) => (b.end - b.start) - (a.end - a.start))
    return { day: day.day, session: day.readable ? recordings[0] || null : null, count: recordings.length }
  })
}

export function healthClock(value, timeZone = "") {
  if (!timestamp(value)) return "–"
  try { return new Intl.DateTimeFormat("de-CH", { hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) }).format(new Date(value)) }
  catch (_) { return new Intl.DateTimeFormat("de-CH", { hour: "2-digit", minute: "2-digit" }).format(new Date(value)) }
}

export function healthDuration(seconds) {
  if (!number(seconds) || seconds < 0) return "–"
  if (seconds > 0 && seconds < 60) return "< 1 min"
  const minutes = Math.round(seconds / 60)
  return minutes >= 60 ? `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min` : `${minutes} min`
}
